import { useState, useEffect, useMemo, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users2,
  BarChart2,
  CalendarIcon,
  Clock3,
  X,
  CheckCircle2,
  Plus,
  Tag,
  FolderPlus,
  Folder,
  ArrowLeft,
  Search,
  Filter,
  Tags,
  Edit2,
  Settings,
  Shield,
  User
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { notifyProjectCreation, notifySubProjectCreation } from '../../lib/notificationService';

// Supabase ayarları
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// Önceden belirlenmiş renkler
const CATEGORY_COLORS = [
  'blue',
  'green',
  'purple',
  'orange',
  'pink',
  'indigo',
  'teal',
  'cyan',
  'red'
];

const Consulting = () => {
  // State tanımlamaları
  const [projects, setProjects] = useState([]);
  const [subProjects, setSubProjects] = useState({});
  const [selectedProjectType, setSelectedProjectType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loadingStates, setLoadingStates] = useState({
    projects: false,
    subProjects: false,
    creation: false,
    categories: false
  });
  const [projectCategories, setProjectCategories] = useState([]);
  const [selectedSubProjects, setSelectedSubProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isNewCategoryModalOpen, setIsNewCategoryModalOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', color: 'blue' });
  const [editingCategory, setEditingCategory] = useState(null);

  // Modal kontrolleri için state'ler
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [selectedProjectForModal, setSelectedProjectForModal] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSubProjectModalOpen, setIsSubProjectModalOpen] = useState(false);
  const [showAllSubProjects, setShowAllSubProjects] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);

  // Alt proje düzenleme ve silme için state'ler
  const [isEditSubProjectModalOpen, setIsEditSubProjectModalOpen] = useState(false);
  const [isDeleteSubProjectModalOpen, setIsDeleteSubProjectModalOpen] = useState(false);
  const [editingSubProject, setEditingSubProject] = useState(null);
  const [subProjectToDelete, setSubProjectToDelete] = useState(null);

  // Yeni proje form state'i
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'active',
    task_count: 0,
    team_size: 0,
    completed_tasks: 0,
    categories: [],
    sub_projects: [],
    has_end_date: true
  });

  // Yeni alt proje form state'i
  const [newSubProject, setNewSubProject] = useState({
    name: '',
    description: '',
    parent_project_id: null,
    start_date: '',
    end_date: '',
    has_end_date: true
  });

  // Alt projeleri filtrele ve sırala
  const getFilteredAndSortedSubProjects = (subProjects, search, sort) => {
    let filtered = subProjects;
    
    // Arama filtrelemesi
    if (search) {
      filtered = subProjects.filter(sp => 
        sp.name.toLowerCase().includes(search.toLowerCase()) ||
        sp.description?.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    // Sıralama
    return filtered.sort((a, b) => {
      switch (sort) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'team-size':
          return (b.team_size || 0) - (a.team_size || 0);
        default:
          return 0;
      }
    });
  };

  // Projeleri çek
  const fetchProjects = async () => {
    try {
      setLoadingStates(prev => ({ ...prev, projects: true }));
      let query = supabase
        .from('consulting')
        .select(`
          *,
          consulting_categories (
            categories(*)
          )
        `);
      
      
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Danışmanlık projeleri alınırken hata oluştu:', error);
        toast.error('Danışmanlık projeleri alınırken bir hata oluştu');
        return;
      }

      // Consulting projelerine bağlı görevleri getir
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('*')
        .not('consulting_parent_id', 'is', null);

      if (tasksError) {
        console.error('Görevler alınırken hata oluştu:', tasksError);
      }

      // Her consulting projesi için dinamik istatistikleri hesapla
      const projectsWithStats = await Promise.all(
        (data || []).map(async (project) => {
          try {
            // Bu projeye bağlı görevleri filtrele (consulting_parent_id ile)
            const projectTasks = (tasksData || []).filter(task => 
              task.consulting_parent_id === project.id
            );

            // Görev sayısı
            const taskCount = projectTasks.length;

            // Tamamlanan görev sayısı
            const completedTasks = projectTasks.filter(task => task.status === 'done').length;

            // Atanmış üyeleri topla (unique)
            const assignedMemberIds = new Set();
            projectTasks.forEach(task => {
              if (task.assigned_members) {
                try {
                  const members = typeof task.assigned_members === 'string' 
                    ? JSON.parse(task.assigned_members) 
                    : task.assigned_members;
                  
                  if (Array.isArray(members)) {
                    members.forEach(member => {
                      const memberId = typeof member === 'object' ? member.id : member;
                      if (memberId) assignedMemberIds.add(memberId);
                    });
                  }
                } catch (e) {
                  console.error('Üye verisi parse edilemedi:', e);
                }
              }
            });

            // Takım büyüklüğü
            const teamSize = assignedMemberIds.size;

            return {
              ...project,
              task_count: taskCount,
              completed_tasks: completedTasks,
              team_size: teamSize
            };
          } catch (err) {
            console.error('Proje istatistikleri hesaplanırken hata:', err);
            return {
              ...project,
              task_count: 0,
              completed_tasks: 0,
              team_size: 0
            };
          }
        })
      );

      // Arama filtrelemesi
      const filteredData = searchQuery
        ? projectsWithStats.filter(project =>
            project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            project.description?.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : projectsWithStats;

      setProjects(filteredData || []);
    } catch (err) {
      console.error('Danışmanlık projeleri alınırken hata oluştu:', err);
      toast.error('Danışmanlık projeleri alınırken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, projects: false }));
    }
  };

  // Proje kategorilerini çek
  const fetchProjectCategories = async (projectId) => {
    try {
      const { data, error } = await supabase
        .from('consulting_categories')
        .select(`
          *,
          category:categories(*)
        `)
        .eq('consulting_id', projectId);

      if (error) {
        console.error('Kategoriler alınırken hata oluştu:', error);
        return;
      }

      setProjectCategories(data || []);
    } catch (err) {
      console.error('Kategoriler alınırken hata oluştu:', err);
    }
  };

  // Alt projeleri çek
  const fetchSubProjects = async (projectId) => {
    try {
      setLoadingStates(prev => ({ ...prev, subProjects: true }));
      const { data, error } = await supabase
        .from('consulting_sub_projects')
        .select('*')
        .eq('parent_consulting_id', projectId);

      if (error) {
        console.error('Alt projeler alınırken hata oluştu:', error);
        return;
      }

      setSubProjects((prev) => ({
        ...prev,
        [projectId]: data,
      }));
    } catch (err) {
      console.error('Alt projeler alınırken hata oluştu:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, subProjects: false }));
    }
  };

  // Proje kartına tıklanınca modal aç ve ilgili alt projeleri çek
  const handleProjectClick = async (project) => {
    try {
      setSelectedProjectForModal(project);
      setIsProjectModalOpen(true);
      setShowAllSubProjects(false);
      setLoadingStates(prev => ({ ...prev, subProjects: true }));
      
      // Alt projeleri çek - DÜZELTILMIŞ SORGU
      const { data: projectSubProjects, error: projectSubProjectsError } = await supabase
        .from('consulting_sub_projects')
        .select('*')  // Tüm alanları seç
        .eq('parent_consulting_id', project.id);

      if (projectSubProjectsError) {
        console.error('Alt projeler alınırken hata:', projectSubProjectsError);
        toast.error('Alt projeler alınırken bir hata oluştu');
        return;
      }

      // Alt projelere bağlı görevleri getir
      const { data: subProjectTasksData, error: subProjectTasksError } = await supabase
        .from('tasks')
        .select('*')
        .not('consulting_project_id', 'is', null);

      if (subProjectTasksError) {
        console.error('Alt proje görevleri alınırken hata:', subProjectTasksError);
      }

      // Her alt proje için dinamik istatistikleri hesapla
      const subProjectsWithStats = (projectSubProjects || []).map(subProject => {
        try {
          // Bu alt projeye bağlı görevleri filtrele (consulting_project_id ile)
          const subProjectTasks = (subProjectTasksData || []).filter(task => 
            task.consulting_project_id === subProject.id
          );

          // Görev sayısı
          const taskCount = subProjectTasks.length;

          // Tamamlanan görev sayısı
          const completedTasks = subProjectTasks.filter(task => task.status === 'done').length;

          // Atanmış üyeleri topla (unique)
          const assignedMemberIds = new Set();
          subProjectTasks.forEach(task => {
            if (task.assigned_members) {
              try {
                const members = typeof task.assigned_members === 'string' 
                  ? JSON.parse(task.assigned_members) 
                  : task.assigned_members;
                
                if (Array.isArray(members)) {
                  members.forEach(member => {
                    const memberId = typeof member === 'object' ? member.id : member;
                    if (memberId) assignedMemberIds.add(memberId);
                  });
                }
              } catch (e) {
                console.error('Alt proje üye verisi parse edilemedi:', e);
              }
            }
          });

          // Takım büyüklüğü
          const teamSize = assignedMemberIds.size;

          return {
            ...subProject,
            task_count: taskCount,
            completed_tasks: completedTasks,
            team_size: teamSize
          };
        } catch (err) {
          console.error('Alt proje istatistikleri hesaplanırken hata:', err);
          return {
            ...subProject,
            task_count: 0,
            completed_tasks: 0,
            team_size: 0
          };
        }
      });

      // Kategorileri çek
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('consulting_categories')
        .select(`
          *,
          category:categories(*)
        `)
        .eq('consulting_id', project.id);

      if (categoriesError) {
        console.error('Kategoriler alınırken hata:', categoriesError);
        toast.error('Kategoriler alınırken bir hata oluştu');
        return;
      }

      // Verileri güncelle
      setSubProjects(prev => ({
        ...prev,
        [project.id]: subProjectsWithStats
      }));
      setSelectedSubProjects(subProjectsWithStats?.map(psp => psp.id) || []);
      setProjectCategories(categoriesData || []);

    } catch (err) {
      console.error('Danışmanlık detayları alınırken hata:', err);
      toast.error('Danışmanlık detayları alınırken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, subProjects: false }));
    }
  };

  // Alt projeleri çek ve seçili olarak ayarla
  const handleSubProjectModalOpen = async (project) => {
    try {
      setLoadingStates(prev => ({ ...prev, subProjects: true }));
      
      // Tüm alt projeleri çek
      const { data: allSubProjects, error: allSubProjectsError } = await supabase
        .from('sub_projects')
        .select('*')
        .neq('status', 'deleted');

      if (allSubProjectsError) {
        console.error('Alt projeler alınırken hata:', allSubProjectsError);
        toast.error('Alt projeler alınırken bir hata oluştu');
        return;
      }

      // Tüm görevleri getir
      const { data: allTasksData, error: allTasksError } = await supabase
        .from('tasks')
        .select('*');

      if (allTasksError) {
        console.error('Görevler alınırken hata:', allTasksError);
      }

      // Alt projeler için dinamik istatistikleri hesapla
      const allSubProjectsWithStats = (allSubProjects || []).map(subProject => {
        try {
          // Bu alt projeye bağlı görevleri filtrele
          const subProjectTasks = (allTasksData || []).filter(task => 
            task.sub_project_id === subProject.id
          );

          // Görev sayısı
          const taskCount = subProjectTasks.length;

          // Tamamlanan görev sayısı
          const completedTasks = subProjectTasks.filter(task => task.status === 'done').length;

          // Atanmış üyeleri topla (unique)
          const assignedMemberIds = new Set();
          subProjectTasks.forEach(task => {
            if (task.assigned_members) {
              try {
                const members = typeof task.assigned_members === 'string' 
                  ? JSON.parse(task.assigned_members) 
                  : task.assigned_members;
                
                if (Array.isArray(members)) {
                  members.forEach(member => {
                    const memberId = typeof member === 'object' ? member.id : member;
                    if (memberId) assignedMemberIds.add(memberId);
                  });
                }
              } catch (e) {
                console.error('Alt proje üye verisi parse edilemedi:', e);
              }
            }
          });

          // Takım büyüklüğü
          const teamSize = assignedMemberIds.size;

          return {
            ...subProject,
            task_count: taskCount,
            completed_tasks: completedTasks,
            team_size: teamSize
          };
        } catch (err) {
          console.error('Alt proje istatistikleri hesaplanırken hata:', err);
          return {
            ...subProject,
            task_count: 0,
            completed_tasks: 0,
            team_size: 0
          };
        }
      });

      // Mevcut projenin alt projelerini çek
      const { data: currentSubProjects, error: currentSubProjectsError } = await supabase
        .from('consulting_sub_projects')
        .select('sub_project_id')
        .eq('consulting_id', project.id);

      if (currentSubProjectsError) {
        console.error('Alt projeler alınırken hata:', currentSubProjectsError);
        toast.error('Alt projeler alınırken bir hata oluştu');
        return;
      }

      // Tüm alt projeleri state'e kaydet
      setSubProjects(prev => ({
        ...prev,
        all: allSubProjectsWithStats,
      }));

      // Mevcut projenin alt projelerinin ID'lerini seçili olarak ayarla
      setSelectedSubProjects(currentSubProjects?.map(sp => sp.sub_project_id) || []);
      setIsSubProjectModalOpen(true);

    } catch (err) {
      console.error('Alt projeler alınırken hata:', err);
      toast.error('Alt projeler alınırken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, subProjects: false }));
    }
  };

  // Alt projeleri güncelle
  const handleUpdateSubProjects = async (projectId, selectedIds) => {
    try {
      setLoadingStates(prev => ({ ...prev, subProjects: true }));

      // Önce mevcut projenin tüm alt proje ilişkilerini sil
      const { error: deleteError } = await supabase
        .from('consulting_sub_projects')
        .delete()
        .eq('parent_consulting_id', projectId);  // consulting_id yerine parent_consulting_id

      if (deleteError) {
        console.error('Alt projeler silinirken hata:', deleteError);
        toast.error('Alt projeler güncellenirken bir hata oluştu');
        return;
      }

      // Seçili alt projeleri ekle
      if (selectedIds.length > 0) {
        const subProjectData = selectedIds.map(subProjectId => ({
          parent_consulting_id: projectId,  // consulting_id yerine parent_consulting_id
          name: subProjects.all.find(sp => sp.id === subProjectId)?.name || '',
          description: subProjects.all.find(sp => sp.id === subProjectId)?.description || '',
          status: 'active',
          team_size: 0,
          task_count: 0,
          completed_tasks: 0,
          start_date: new Date().toISOString().split('T')[0],
          end_date: null
        }));

        const { error: insertError } = await supabase
          .from('consulting_sub_projects')
          .insert(subProjectData);

        if (insertError) {
          console.error('Alt projeler eklenirken hata:', insertError);
          toast.error('Alt projeler güncellenirken bir hata oluştu');
          return;
        }
      }

      // Başarılı mesajı göster
      toast.success('Alt projeler başarıyla güncellendi');

      // Alt projeleri yeniden yükle
      await handleProjectClick({ id: projectId });

    } catch (err) {
      console.error('Alt projeler güncellenirken hata:', err);
      toast.error('Alt projeler güncellenirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, subProjects: false }));
    }
  };

  // Yeni proje oluştur
  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error('Firma adı boş olamaz');
      return;
    }

    if (!newProject.start_date) {
      toast.error('Başlangıç tarihi seçilmeli');
      return;
    }

    // Tarih validasyonu
    if (newProject.has_end_date && newProject.end_date && newProject.start_date) {
      const startDate = new Date(newProject.start_date);
      const endDate = new Date(newProject.end_date);
      
      if (endDate < startDate) {
        toast.error('Bitiş tarihi, başlangıç tarihinden önce olamaz');
        return;
      }
    }

    // Eğer bitiş tarihi belirle seçiliyse ama tarih girilmemişse uyar
    if (newProject.has_end_date && !newProject.end_date) {
      toast.error('Bitiş tarihi belirle seçeneği aktifken bitiş tarihi seçmelisiniz');
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, creation: true }));

      // Kullanıcı bilgisini al
      const { data: { user } } = await supabase.auth.getUser();

      const projectData = {
        name: newProject.name,
        description: newProject.description,
        start_date: newProject.start_date,
        end_date: newProject.has_end_date && newProject.end_date ? newProject.end_date : null,
        status: 'active',
        created_by: user.id,
        task_count: 0,
        team_size: 0,
        completed_tasks: 0
      };

      // Projeyi oluştur
      const { data: project, error: projectError } = await supabase
        .from('consulting')
        .insert([projectData])
        .select()
        .single();

      if (projectError) throw projectError;

      // Seçili kategorileri ekle
      if (newProject.categories.length > 0) {
        const categoryData = newProject.categories.map(categoryId => ({
          consulting_id: project.id,
          category_id: categoryId
        }));

        const { error: categoryError } = await supabase
          .from('consulting_categories')
          .insert(categoryData);

        if (categoryError) throw categoryError;
      }

      // Seçili alt projeleri ekle
      if (newProject.sub_projects.length > 0) {
        const subProjectData = newProject.sub_projects.map(subProjectId => ({
          consulting_id: project.id,
          sub_project_id: subProjectId
        }));

        const { error: subProjectError } = await supabase
          .from('consulting_sub_projects')
          .insert(subProjectData);

        if (subProjectError) throw subProjectError;
      }

      // Kullanıcı profil bilgilerini al
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();

      if (!profileError && userProfile) {
        const creatorName = `${userProfile.first_name} ${userProfile.last_name}`;
        
        // Bildirimleri oluştur
        try {
          await notifyProjectCreation(project, user.id, creatorName, 'consulting');
          console.log('Danışmanlık bildirimleri başarıyla gönderildi');
        } catch (notificationError) {
          console.error('Bildirim gönderme hatası:', notificationError);
          // Bildirim hatası proje oluşturmayı engellemez
        }
      }

      // Projeleri yeniden yükle
      await fetchProjects();
      
      setIsCreateModalOpen(false);
      setSelectedProjectType(null);
      setNewProject({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        status: 'active',
        task_count: 0,
        team_size: 0,
        completed_tasks: 0,
        categories: [],
        sub_projects: [],
        has_end_date: true
      });
      toast.success('Danışmanlık başarıyla oluşturuldu');
    } catch (err) {
      console.error('Danışmanlık oluşturulurken hata:', err);
      toast.error('Danışmanlık oluşturulurken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  // Alt proje oluştur
  const handleCreateSubProject = async () => {
    if (!newSubProject.name.trim()) {
      toast.error('Alt başlık adı boş olamaz');
      return;
    }

    if (!newSubProject.parent_project_id) {
      toast.error('Lütfen bir danışmanlık seçin');
      return;
    }

    if (!newSubProject.start_date) {
      toast.error('Başlangıç tarihi seçilmeli');
      return;
    }

    // Tarih validasyonu
    if (newSubProject.has_end_date && newSubProject.end_date && newSubProject.start_date) {
      const startDate = new Date(newSubProject.start_date);
      const endDate = new Date(newSubProject.end_date);
      
      if (endDate < startDate) {
        toast.error('Bitiş tarihi, başlangıç tarihinden önce olamaz');
        return;
      }
    }

    // Eğer bitiş tarihi belirle seçiliyse ama tarih girilmemişse uyar
    if (newSubProject.has_end_date && !newSubProject.end_date) {
      toast.error('Bitiş tarihi belirle seçeneği aktifken bitiş tarihi seçmelisiniz');
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, creation: true }));

      // Kullanıcı bilgisini al
      const { data: { user } } = await supabase.auth.getUser();

      const subProjectData = {
        name: newSubProject.name,
        description: newSubProject.description || '',
        parent_consulting_id: newSubProject.parent_project_id,
        status: 'active',
        team_size: 0,
        task_count: 0,
        completed_tasks: 0,
        start_date: newSubProject.start_date,
        end_date: newSubProject.has_end_date && newSubProject.end_date ? newSubProject.end_date : null
      };

      const { data: subProject, error: subProjectError } = await supabase
        .from('consulting_sub_projects')
        .insert([subProjectData])
        .select()
        .single();

      if (subProjectError) throw subProjectError;

      // Ana projenin adını al
      const { data: parentProject, error: parentError } = await supabase
        .from('consulting')
        .select('name')
        .eq('id', newSubProject.parent_project_id)
        .single();

      if (!parentError && parentProject) {
        try {
          await notifySubProjectCreation(
            subProject, 
            newSubProject.parent_project_id, 
            parentProject.name, 
            user.id,
            'consulting' // Belirt ki bu bir consulting projesidir
          );
          //console.log('Alt başlık bildirimleri başarıyla gönderildi');
        } catch (notificationError) {
          console.error('Alt başlık bildirim hatası:', notificationError);
          // Bildirim hatası alt proje oluşturmayı engellemez
        }
      }

      // Alt projeleri yeniden yükle ve istatistikleri güncelle
      await fetchSubProjects(newSubProject.parent_project_id);
      
      // Ana proje detaylarını güncelle (istatistikler için)
      if (selectedProjectForModal && selectedProjectForModal.id === newSubProject.parent_project_id) {
        await handleProjectClick(selectedProjectForModal);
      }
      
      setIsCreateModalOpen(false);
      setSelectedProjectType(null);
      setNewSubProject({
        name: '',
        description: '',
        parent_project_id: null,
        start_date: '',
        end_date: '',
        has_end_date: true
      });
      toast.success('Alt başlık başarıyla oluşturuldu');
    } catch (err) {
      console.error('Alt başlık oluşturulurken hata:', err);
      toast.error('Alt başlık oluşturulurken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  const [subProjectSearch, setSubProjectSearch] = useState('');
  const [subProjectSort, setSubProjectSort] = useState('name-asc'); // 'name-asc', 'name-desc', 'status', 'team-size'

  // Kategorileri çek
  const fetchCategories = async () => {
    try {
      setLoadingStates(prev => ({ ...prev, categories: true }));
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (error) {
        console.error('Kategoriler alınırken hata:', error);
        toast.error('Kategoriler alınırken bir hata oluştu');
        return;
      }

      setCategories(data || []);
    } catch (err) {
      console.error('Kategoriler alınırken hata:', err);
      toast.error('Kategoriler alınırken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, categories: false }));
    }
  };

  // Yeni kategori oluştur
  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Kategori adı boş olamaz');
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, categories: true }));
      
      // Rastgele bir renk seç
      const randomColor = CATEGORY_COLORS[Math.floor(Math.random() * CATEGORY_COLORS.length)];
      
      const { data, error } = await supabase
        .from('categories')
        .insert([{
          name: newCategory.name,
          color: randomColor
        }])
        .select()
        .single();

      if (error) throw error;

      setCategories(prev => [...prev, data]);
      setNewCategory({ name: '', color: 'blue' });
      setIsNewCategoryModalOpen(false);
      toast.success('Kategori başarıyla oluşturuldu');
    } catch (err) {
      console.error('Kategori oluşturulurken hata:', err);
      toast.error('Kategori oluşturulurken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, categories: false }));
    }
  };

  // Kategori güncelle
  const handleUpdateCategory = async (categoryId) => {
    if (!editingCategory.name.trim()) {
      toast.error('Kategori adı boş olamaz');
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, categories: true }));
      const { error } = await supabase
        .from('categories')
        .update({
          name: editingCategory.name,
          color: editingCategory.color
        })
        .eq('id', categoryId);

      if (error) throw error;

      setCategories(prev => prev.map(cat => 
        cat.id === categoryId ? { ...cat, ...editingCategory } : cat
      ));
      setEditingCategory(null);
      toast.success('Kategori başarıyla güncellendi');
    } catch (err) {
      console.error('Kategori güncellenirken hata:', err);
      toast.error('Kategori güncellenirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, categories: false }));
    }
  };

  // Kategori sil
  const handleDeleteCategory = async (categoryId) => {
    try {
      setLoadingStates(prev => ({ ...prev, categories: true }));
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      setCategories(prev => prev.filter(cat => cat.id !== categoryId));
      toast.success('Kategori başarıyla silindi');
    } catch (err) {
      console.error('Kategori silinirken hata:', err);
      toast.error('Kategori silinirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, categories: false }));
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [searchQuery, statusFilter, selectedProjectType]);

  useEffect(() => {
    if (selectedProjectType === 'categories') {
      fetchCategories();
    }
  }, [selectedProjectType]);

  // Modal açıldığında body scroll'u engelle
  useEffect(() => {
    if (isProjectModalOpen || isCreateModalOpen || isSubProjectModalOpen || isNewCategoryModalOpen || isEditModalOpen || isDeleteModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    // Component unmount olduğunda overflow'u tekrar auto yap
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isProjectModalOpen, isCreateModalOpen, isSubProjectModalOpen, isNewCategoryModalOpen, isEditModalOpen, isDeleteModalOpen]);

  // Yeni proje modalı açıldığında kategorileri ve alt projeleri yükle
  useEffect(() => {
    if (isCreateModalOpen && selectedProjectType === 'project') {
      // Kategorileri yükle
      fetchCategories();
      
      // Tüm aktif alt projeleri yükle
      const fetchAvailableSubProjects = async () => {
        try {
          setLoadingStates(prev => ({ ...prev, subProjects: true }));
          const { data, error } = await supabase
            .from('consulting_sub_projects')
            .select('*')
            .eq('status', 'active');

          if (error) throw error;

          setSubProjects(prev => ({
            ...prev,
            all: data || []
          }));
        } catch (err) {
          console.error('Alt projeler yüklenirken hata:', err);
          toast.error('Alt projeler yüklenirken bir hata oluştu');
        } finally {
          setLoadingStates(prev => ({ ...prev, subProjects: false }));
        }
      };

      fetchAvailableSubProjects();
    }
  }, [isCreateModalOpen, selectedProjectType]);

  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showOnlyMyConsulting, setShowOnlyMyConsulting] = useState(false);

  // Fetch current user and role
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        setCurrentUser(userProfile);
        setUserRole(userProfile?.role);
        if (userProfile?.role !== 'super') {
          setShowOnlyMyConsulting(true);
        }
      }
    };
    getCurrentUser();
  }, []);

  // Permission-based consulting filtering
  const permissionFilteredConsulting = useMemo(() => {
    return projects.filter(project => {
      if (!currentUser || !userRole) return false;
      if (userRole === 'super' && !showOnlyMyConsulting) return true;
      if (userRole === 'super' && showOnlyMyConsulting) {
        // Only show consulting where user is assigned (if assignment exists)
        return project.assigned_members && project.assigned_members.some(m => m.id === currentUser.id);
      }
      if (userRole === 'admin') {
        // Admins see consulting they created or are assigned to
        return (project.created_by === currentUser.id) || (project.assigned_members && project.assigned_members.some(m => m.id === currentUser.id));
      }
      // Regular users see only assigned consulting
      return project.assigned_members && project.assigned_members.some(m => m.id === currentUser.id);
    });
  }, [projects, currentUser, userRole, showOnlyMyConsulting]);

  // Danışmanlık düzenle
  const handleEditProject = async () => {
    if (!editingProject.name.trim()) {
      toast.error('Firma adı boş olamaz');
      return;
    }

    if (!editingProject.start_date) {
      toast.error('Başlangıç tarihi seçilmeli');
      return;
    }

    // Tarih validasyonu
    if (editingProject.has_end_date && editingProject.end_date && editingProject.start_date) {
      const startDate = new Date(editingProject.start_date);
      const endDate = new Date(editingProject.end_date);
      
      if (endDate < startDate) {
        toast.error('Bitiş tarihi, başlangıç tarihinden önce olamaz');
        return;
      }
    }

    // Eğer bitiş tarihi belirle seçiliyse ama tarih girilmemişse uyar
    if (editingProject.has_end_date && !editingProject.end_date) {
      toast.error('Bitiş tarihi belirle seçeneği aktifken bitiş tarihi seçmelisiniz');
      return;
    }

    try {
      setLoadingStates(prev => ({ ...prev, creation: true }));

      const projectData = {
        name: editingProject.name,
        description: editingProject.description,
        start_date: editingProject.start_date,
        end_date: editingProject.has_end_date && editingProject.end_date ? editingProject.end_date : null,
        status: editingProject.status
      };

      // Danışmanlığı güncelle
      const { error: projectError } = await supabase
        .from('consulting')
        .update(projectData)
        .eq('id', editingProject.id);

      if (projectError) throw projectError;

      // Danışmanlıkları yeniden yükle
      await fetchProjects();
      
      setIsEditModalOpen(false);
      setEditingProject(null);
      toast.success('Danışmanlık başarıyla güncellendi');
    } catch (err) {
      console.error('Danışmanlık güncellenirken hata:', err);
      toast.error('Danışmanlık güncellenirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  // Danışmanlık sil
  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      setLoadingStates(prev => ({ ...prev, creation: true }));

      // Önce consulting_categories tablosundan bu danışmanlıkla ilgili kayıtları sil
      const { error: consultingCategoriesError } = await supabase
        .from('consulting_categories')
        .delete()
        .eq('consulting_id', projectToDelete.id);

      if (consultingCategoriesError) {
        console.error('Danışmanlık kategorileri silinirken hata:', consultingCategoriesError);
        toast.error('Danışmanlık kategorileri silinirken bir hata oluştu');
        return;
      }

      // consulting_sub_projects tablosundan bu danışmanlıkla ilgili kayıtları sil
      const { error: consultingSubProjectsError } = await supabase
        .from('consulting_sub_projects')
        .delete()
        .eq('parent_consulting_id', projectToDelete.id);

      if (consultingSubProjectsError) {
        console.error('Danışmanlık alt projeleri silinirken hata:', consultingSubProjectsError);
        toast.error('Danışmanlık alt projeleri silinirken bir hata oluştu');
        return;
      }

      // Sonra danışmanlığı gerçekten sil (hard delete)
      const { error } = await supabase
        .from('consulting')
        .delete()
        .eq('id', projectToDelete.id);

      if (error) throw error;

      // Danışmanlıkları yeniden yükle
      await fetchProjects();
      
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
      toast.success('Danışmanlık başarıyla silindi');
    } catch (err) {
      console.error('Danışmanlık silinirken hata:', err);
      toast.error('Danışmanlık silinirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  // Düzenleme modalını aç
  const openEditModal = (project) => {
    setEditingProject({
      ...project,
      has_end_date: !!project.end_date
    });
    setIsEditModalOpen(true);
  };

  // Silme modalını aç
  const openDeleteModal = (project) => {
    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
  };

  // Alt proje düzenleme modalını aç
  const openEditSubProjectModal = (subProject) => {
    setEditingSubProject({
      ...subProject,
      has_end_date: !!subProject.end_date
    });
    setIsEditSubProjectModalOpen(true);
  };

  // Alt proje silme modalını aç
  const openDeleteSubProjectModal = (subProject) => {
    setSubProjectToDelete(subProject);
    setIsDeleteSubProjectModalOpen(true);
  };

  // Alt proje düzenleme
  const handleEditSubProject = async () => {
    try {
      if (!editingSubProject.name.trim()) {
        toast.error('Proje adı boş olamaz');
        return;
      }

      if (!editingSubProject.start_date) {
        toast.error('Başlangıç tarihi seçilmelidir');
        return;
      }

      if (editingSubProject.has_end_date && !editingSubProject.end_date) {
        toast.error('Bitiş tarihi seçilmelidir');
        return;
      }

      setLoadingStates(prev => ({ ...prev, creation: true }));

      const updateData = {
        name: editingSubProject.name.trim(),
        description: editingSubProject.description?.trim() || null,
        start_date: editingSubProject.start_date,
        end_date: editingSubProject.has_end_date ? editingSubProject.end_date : null,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('consulting_sub_projects')
        .update(updateData)
        .eq('id', editingSubProject.id);

      if (error) {
        console.error('Alt proje güncellenirken hata:', error);
        toast.error('Alt proje güncellenirken bir hata oluştu');
        return;
      }

      toast.success('Alt proje başarıyla güncellendi');
      setIsEditSubProjectModalOpen(false);
      setEditingSubProject(null);
      
      // Alt projeleri yeniden yükle
      if (selectedProjectForModal) {
        await handleProjectClick(selectedProjectForModal);
      }

    } catch (err) {
      console.error('Alt proje güncellenirken hata:', err);
      toast.error('Alt proje güncellenirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  // Alt proje silme
  const handleDeleteSubProject = async () => {
    try {
      if (!subProjectToDelete) return;

      setLoadingStates(prev => ({ ...prev, creation: true }));

      // Alt projeye bağlı görevleri kontrol et
      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select('id')
        .eq('consulting_project_id', subProjectToDelete.id);

      if (tasksError) {
        console.error('Görevler kontrol edilirken hata:', tasksError);
        toast.error('Alt proje silinirken bir hata oluştu');
        return;
      }

      if (tasks && tasks.length > 0) {
        toast.error('Bu alt projeye bağlı görevler bulunmaktadır. Önce görevleri silin.');
        return;
      }

      const { error } = await supabase
        .from('consulting_sub_projects')
        .delete()
        .eq('id', subProjectToDelete.id);

      if (error) {
        console.error('Alt proje silinirken hata:', error);
        toast.error('Alt proje silinirken bir hata oluştu');
        return;
      }

      toast.success('Alt proje başarıyla silindi');
      setIsDeleteSubProjectModalOpen(false);
      setSubProjectToDelete(null);
      
      // Alt projeleri yeniden yükle
      if (selectedProjectForModal) {
        await handleProjectClick(selectedProjectForModal);
      }

    } catch (err) {
      console.error('Alt proje silinirken hata:', err);
      toast.error('Alt proje silinirken bir hata oluştu');
    } finally {
      setLoadingStates(prev => ({ ...prev, creation: false }));
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="flex justify-between items-center mt-2 mb-8">
        {/* Permission level indicator */}
        {userRole && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
            userRole === 'super' 
              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' 
              : userRole === 'admin' 
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
          }`}>
            {userRole === 'super' ? (
              <Shield className="w-4 h-4" />
            ) : userRole === 'admin' ? (
              <User className="w-4 h-4" />
            ) : (
              <Users2 className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {userRole === 'super' 
                ? 'Süper Yönetici' 
                : userRole === 'admin' 
                  ? 'Yönetici' 
                  : 'Standart Kullanıcı'
              }
            </span>
          </div>
        )}
        {/* Super user filter toggle */}
        {userRole === 'super' && (
          <div className="relative mr-2">
            <button
              type="button"
              onClick={() => setShowOnlyMyConsulting(!showOnlyMyConsulting)}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                showOnlyMyConsulting
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
              }`}
            >
              {showOnlyMyConsulting ? (
                <Users2 className="w-4 h-4" />
              ) : (
                <Folder className="w-4 h-4" />
              )}
              {showOnlyMyConsulting ? 'Danışmanlıklarım' : 'Tüm Danışmanlıklar'}
            </button>
          </div>
        )}
      </div>

      {/* Danışmanlık Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="mb-6 space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Danışmanlık
            </h3>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Toplam: {projects.length} Danışmanlık Mevcut
            </div>
          </div>

          {/* Arama ve Filtreleme */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input 
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Danışmanlık ara..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-center gap-2 min-w-[200px]">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">Tüm Durumlar</option>
                <option value="active">Aktif</option>
                <option value="completed">Tamamlandı</option>
                <option value="pending">Beklemede</option>
              </select>
            </div>
          </div>
        </div>

        {loadingStates.projects ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <motion.div
                key={project.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 cursor-pointer transition-colors relative group"
              >
                {/* Düzenle ve Sil Butonları - Sadece super user için */}
                {userRole === 'super' && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity mt-12">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(project);
                      }}
                      className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                      title="Danışmanlığı Düzenle"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(project);
                      }}
                      className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
                      title="Danışmanlığı Sil"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <div 
                onClick={() => handleProjectClick(project)}
                  className="cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                    <h4 className="text-base font-medium text-gray-900 dark:text-white pr-16">
                    {project.name}
                  </h4>
                  <div
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      project.status === 'active'
                        ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                        : project.status === 'completed'
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                    }`}
                  >
                    {project.status === 'active'
                      ? 'Aktif'
                      : project.status === 'completed'
                      ? 'Tamamlandı'
                      : 'Beklemede'}
                  </div>
                </div>

                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2 mt-4 mr-12 ">
                  {project.description || 'Açıklama bulunmuyor'}
                </p>

                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <Users2 className="w-4 h-4" />
                    <span>{project.team_size || 0} Üye</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BarChart2 className="w-4 h-4" />
                    <span>{project.task_count || 0} Görev</span>
                  </div>
                </div>
                {project.consulting_categories && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {project.consulting_categories.map(({ categories }) => (
                    <span
                      key={categories.id}
                      className={`px-2 py-1 text-xs font-medium rounded-full bg-${categories.color}-50 text-${categories.color}-700 dark:bg-${categories.color}-900/20 dark:text-${categories.color}-400`}
                    >
                      {categories.name}
                    </span>
                  ))}
                </div>
              )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="mb-4">
              <Folder className="w-12 h-12 mx-auto text-gray-400 animate-pulse" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 animate-pulse">
              Danışmanlık Bulunamadı
            </h3>
            <p className="text-gray-500 dark:text-gray-400 animate-pulse">
              {searchQuery || statusFilter !== 'all'
                ? 'Arama kriterlerinize uygun danışmanlık bulunamadı. Filtreleri değiştirmeyi deneyin.'
                : 'Henüz hiç danışmanlık oluşturulmamış. Yeni bir danışmanlık oluşturmak için "Yeni Oluştur" butonuna tıklayın.'}
            </p>
          </div>
        )}
      </div>

      {/* Yeni Danışmanlık Butonu */}
      <div className="flex justify-center mt-6">
        {userRole === 'super' ? (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="w-full max-w-4xl flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
          >
            <Plus className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <span className="text-gray-600 dark:text-gray-300">Yeni Oluştur</span>
          </button>
        ) : (
          <button
            disabled
            className="w-full max-w-4xl flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-400 cursor-not-allowed opacity-60"
            title="Sadece Süper Yönetici yeni danışmanlık oluşturabilir"
          >
            <Plus className="w-5 h-5 text-gray-400" />
            <span className="text-gray-400">Yeni Oluştur</span>
          </button>
        )}
      </div>

      {/* Danışmanlık Detay Modal */}
      <AnimatePresence>
        {isProjectModalOpen && selectedProjectForModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Modal arkaplanı */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsProjectModalOpen(false);
                setSelectedProjectForModal(null);
                setSelectedSubProjects([]);
                setShowAllSubProjects(false);
              }}
            />
            {/* Modal içeriği */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[95vw] max-w-6xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden"
            >
              <div className="flex flex-col h-full max-h-[90vh]">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 flex-shrink-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2 rounded-lg ${
                          selectedProjectForModal.status === 'active'
                            ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                            : selectedProjectForModal.status === 'completed'
                            ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          <Folder className="w-5 h-5" />
                        </div>
                  <div>
                          <span className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-1 block">
                            Danışmanlık Detayları
                          </span>
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedProjectForModal.name}
                    </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-1">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        <span>
                                Başlangıç: {selectedProjectForModal.start_date
                                  ? new Date(selectedProjectForModal.start_date).toLocaleDateString('tr-TR')
                                  : 'Belirtilmemiş'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock3 className="w-4 h-4" />
                        <span>
                                Bitiş: {selectedProjectForModal.end_date
                            ? new Date(selectedProjectForModal.end_date).toLocaleDateString('tr-TR')
                            : 'Belirsiz'}
                        </span>
                      </div>
                            <div className={`px-3 py-1 text-xs font-medium rounded-full ${
                              selectedProjectForModal.status === 'active'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : selectedProjectForModal.status === 'completed'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {selectedProjectForModal.status === 'active'
                                ? 'Aktif'
                                : selectedProjectForModal.status === 'completed'
                                ? 'Tamamlandı'
                                : 'Beklemede'}
                    </div>
                  </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {userRole === 'super' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(selectedProjectForModal);
                            setIsProjectModalOpen(false);
                          }}
                          className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                          title="Danışmanlığı Düzenle"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                  <button
                    onClick={() => {
                      setIsProjectModalOpen(false);
                      setSelectedProjectForModal(null);
                      setSelectedSubProjects([]);
                      setShowAllSubProjects(false);
                    }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 min-h-0 custom-scrollbar">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Sol Sütun - Ana Bilgiler */}
                    <div className="lg:col-span-2 space-y-6">
                      {/* Firma Açıklaması */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <Tag className="w-5 h-5 text-primary-500" />
                      Firma Açıklaması
                    </h4>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                          {selectedProjectForModal.description || 'Bu firma için henüz bir açıklama eklenmemiş. Firma hakkında detaylı bilgi eklemek için düzenleme yapabilirsiniz.'}
                    </p>
                  </div>

                      {/* İlerleme Durumu */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <BarChart2 className="w-5 h-5 text-primary-500" />
                          Proje İlerlemesi
                    </h4>
                        
                        {/* İlerleme Çubuğu */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Tamamlanma Oranı
                          </span>
                            <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                              {selectedProjectForModal.task_count > 0 
                                ? Math.round((selectedProjectForModal.completed_tasks / selectedProjectForModal.task_count) * 100)
                                : 0}%
                            </span>
                      </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-primary-500 to-blue-500 h-3 rounded-full transition-all duration-300"
                              style={{
                                width: selectedProjectForModal.task_count > 0 
                                  ? `${Math.round((selectedProjectForModal.completed_tasks / selectedProjectForModal.task_count) * 100)}%`
                                  : '0%'
                              }}
                            />
                          </div>
                        </div>

                        {/* Görev Detayları */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                              {selectedProjectForModal.task_count || 0}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Toplam Görev
                            </div>
                          </div>
                          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                              {selectedProjectForModal.completed_tasks || 0}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Tamamlanan
                            </div>
                          </div>
                          <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                              {(selectedProjectForModal.task_count || 0) - (selectedProjectForModal.completed_tasks || 0)}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Devam Eden
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Zaman Çizelgesi */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <CalendarIcon className="w-5 h-5 text-primary-500" />
                          Danışmanlık Zaman Çizelgesi
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white">Danışmanlık Başlangıcı</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {selectedProjectForModal.start_date
                                  ? new Date(selectedProjectForModal.start_date).toLocaleDateString('tr-TR', {
                                      weekday: 'long',
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })
                                  : 'Tarih belirlenmemiş'}
                              </div>
                            </div>
                          </div>
                          
                          {selectedProjectForModal.end_date && (
                            <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg">
                              <div className={`w-3 h-3 rounded-full ${
                                new Date(selectedProjectForModal.end_date) < new Date() 
                                  ? 'bg-red-500' 
                                  : 'bg-blue-500'
                              }`}></div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {new Date(selectedProjectForModal.end_date) < new Date() 
                                    ? 'Danışmanlık Süresi Doldu' 
                                    : 'Planlanan Bitiş'}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {new Date(selectedProjectForModal.end_date).toLocaleDateString('tr-TR', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Kalan Süre */}
                          {selectedProjectForModal.end_date && (
                            <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                              <div className="text-sm font-medium text-primary-700 dark:text-primary-300">
                                {(() => {
                                  const now = new Date();
                                  const endDate = new Date(selectedProjectForModal.end_date);
                                  const diffTime = endDate - now;
                                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                  
                                  if (diffDays < 0) {
                                    return `Danışmanlık süresi ${Math.abs(diffDays)} gün önce doldu`;
                                  } else if (diffDays === 0) {
                                    return 'Danışmanlık bugün bitiyor';
                                  } else if (diffDays === 1) {
                                    return 'Danışmanlık yarın bitiyor';
                                  } else {
                                    return `Danışmanlığın bitmesine ${diffDays} gün kaldı`;
                                  }
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                  </div>

                      {/* Firmanın Projeleri */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <Folder className="w-5 h-5 text-primary-500" />
                        Firmanın Projeleri
                      </h4>
                    {loadingStates.subProjects ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                            <span>Projeler yükleniyor...</span>
                      </div>
                    ) : subProjects[selectedProjectForModal.id]?.length > 0 ? (
                      <div className="space-y-4">
                        <div className="relative">
                              <div className="grid grid-cols-1 gap-4">
                            {subProjects[selectedProjectForModal.id]
                                  .slice(0, showAllSubProjects ? undefined : 3)
                              .map((subProject) => (
                              <div
                                key={subProject.id}
                                    className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 dark:hover:border-primary-500 transition-all duration-200 hover:shadow-md"
                              >
                                    <div className="flex items-center justify-between mb-3">
                                      <span className="font-medium text-gray-900 dark:text-white text-lg">
                                    {subProject.name}
                                  </span>
                                      <div className="flex items-center gap-2">
                                        {/* Alt proje düzenleme ve silme butonları - Sadece super user için */}
                                        {userRole === 'super' && (
                                          <div className="flex items-center gap-1 mr-2">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openEditSubProjectModal(subProject);
                                              }}
                                              className="p-1 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                                              title="Alt Proje Düzenle"
                                            >
                                              <Edit2 className="w-3 h-3" />
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openDeleteSubProjectModal(subProject);
                                              }}
                                              className="p-1 bg-red-500 hover:bg-red-600 text-white rounded transition-colors"
                                              title="Alt Proje Sil"
                                            >
                                              <X className="w-3 h-3" />
                                            </button>
                                          </div>
                                        )}
                                        <div className={`px-3 py-1 text-xs font-medium rounded-full ${
                                      subProject.status === 'active'
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : subProject.status === 'completed'
                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                        }`}>
                                    {subProject.status === 'active'
                                      ? 'Aktif'
                                      : subProject.status === 'completed'
                                      ? 'Tamamlandı'
                                      : 'Beklemede'}
                                        </div>
                                  </div>
                                </div>
                                {subProject.description && (
                                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 leading-relaxed">
                                    {subProject.description}
                                  </p>
                                )}
                                    <div className="grid grid-cols-3 gap-4">
                                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                        <div className="p-1 bg-blue-100 dark:bg-blue-900/30 rounded">
                                          <Users2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  </div>
                                        <span className="text-sm font-medium">{subProject.team_size || 0} Üye</span>
                                  </div>
                                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                        <div className="p-1 bg-purple-100 dark:bg-purple-900/30 rounded">
                                          <BarChart2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                  </div>
                                        <span className="text-sm font-medium">{subProject.task_count || 0} Görev</span>
                                      </div>
                                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                        <div className="p-1 bg-green-100 dark:bg-green-900/30 rounded">
                                          <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        </div>
                                        <span className="text-sm font-medium">{subProject.completed_tasks || 0} Tamamlanan</span>
                                      </div>
                                </div>
                              </div>
                            ))}
                          </div>
                              {!showAllSubProjects && subProjects[selectedProjectForModal.id].length > 3 && (
                                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 dark:from-gray-700 to-transparent pointer-events-none rounded-b-xl"></div>
                          )}
                        </div>
                            {subProjects[selectedProjectForModal.id].length > 3 && (
                          <button
                            onClick={() => setShowAllSubProjects(!showAllSubProjects)}
                                className="w-full py-3 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                          >
                                {showAllSubProjects ? 'Daha Az Göster' : `${subProjects[selectedProjectForModal.id].length - 3} Proje Daha Göster`}
                          </button>
                        )}
                      </div>
                    ) : (
                          <div className="text-center py-8">
                            <Folder className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500 dark:text-gray-400 animate-pulse">
                              Bu firmaya henüz proje eklenmemiş
                            </p>
                          </div>
                        )}
                      </div>
                  </div>

                    {/* Sağ Sütun - İstatistikler ve Eylemler */}
                    <div className="space-y-6">
                      {/* Hızlı İstatistikler */}
                      <div className="bg-gradient-to-br from-primary-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <BarChart2 className="w-5 h-5 text-primary-500" />
                      Danışmanlık İstatistikleri
                    </h4>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <BarChart2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Toplam Görev
                              </span>
                        </div>
                            <span className="text-xl font-bold text-gray-900 dark:text-white">
                          {selectedProjectForModal.task_count || 0}
                            </span>
                        </div>

                          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                <Users2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                      </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Ekip Üyeleri
                              </span>
                        </div>
                            <span className="text-xl font-bold text-gray-900 dark:text-white">
                          {selectedProjectForModal.team_size || 0}
                            </span>
                        </div>

                          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                <CheckCircle2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Tamamlanan
                              </span>
                        </div>
                            <span className="text-xl font-bold text-gray-900 dark:text-white">
                          {selectedProjectForModal.completed_tasks || 0}
                            </span>
                        </div>

                          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                                <Clock3 className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                      </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Başarı Oranı
                              </span>
                    </div>
                            <span className="text-xl font-bold text-gray-900 dark:text-white">
                              {selectedProjectForModal.task_count > 0 
                                ? Math.round((selectedProjectForModal.completed_tasks / selectedProjectForModal.task_count) * 100)
                                : 0}%
                            </span>
                  </div>
                        </div>
                      </div>

                      {/* Danışmanlık Durumu Kartı */}
                      <div className="bg-white dark:bg-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                          Danışmanlık Durumu
                        </h4>
                        <div className="space-y-3">
                          <div className={`p-4 rounded-lg border ${
                            selectedProjectForModal.status === 'active'
                              ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                              : selectedProjectForModal.status === 'completed'
                              ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                              : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
                          }`}>
                            <div className="flex items-center gap-3 mb-2">
                              <div className={`w-3 h-3 rounded-full ${
                                selectedProjectForModal.status === 'active'
                                  ? 'bg-green-500'
                                  : selectedProjectForModal.status === 'completed'
                                  ? 'bg-blue-500'
                                  : 'bg-yellow-500'
                              }`}></div>
                              <span className={`font-semibold ${
                                selectedProjectForModal.status === 'active'
                                  ? 'text-green-700 dark:text-green-400'
                                  : selectedProjectForModal.status === 'completed'
                                  ? 'text-blue-700 dark:text-blue-400'
                                  : 'text-yellow-700 dark:text-yellow-400'
                              }`}>
                                {selectedProjectForModal.status === 'active'
                                  ? 'Aktif Danışmanlık'
                                  : selectedProjectForModal.status === 'completed'
                                  ? 'Tamamlanan Danışmanlık'
                                  : 'Beklemede'}
                              </span>
                            </div>
                            <p className={`text-sm ${
                              selectedProjectForModal.status === 'active'
                                ? 'text-green-600 dark:text-green-300'
                                : selectedProjectForModal.status === 'completed'
                                ? 'text-blue-600 dark:text-blue-300'
                                : 'text-yellow-600 dark:text-yellow-300'
                            }`}>
                              {selectedProjectForModal.status === 'active'
                                ? 'Danışmanlık aktif olarak devam ediyor'
                                : selectedProjectForModal.status === 'completed'
                                ? 'Danışmanlık başarıyla tamamlandı'
                                : 'Danışmanlık başlatılmayı bekliyor'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Firma Kategorileri */}
                      <div className="bg-white dark:bg-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <Tags className="w-5 h-5 text-primary-500" />
                          Firma Kategorileri
                        </h4>
                        {loadingStates.subProjects ? (
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                            <span>Kategoriler yükleniyor...</span>
                          </div>
                        ) : projectCategories.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {projectCategories.map((projectCategory) => (
                              <span
                                key={projectCategory.id}
                                className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full bg-${projectCategory.category?.color || 'gray'}-50 text-${projectCategory.category?.color || 'gray'}-700 dark:bg-${projectCategory.category?.color || 'gray'}-900/20 dark:text-${projectCategory.category?.color || 'gray'}-400 border border-${projectCategory.category?.color || 'gray'}-200 dark:border-${projectCategory.category?.color || 'gray'}-800`}
                              >
                                <Tags className="w-3 h-3" />
                                {projectCategory.category?.name || 'Kategori Silinmiş'}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <Tags className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
                              Bu firmaya henüz kategori eklenmemiş
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Hızlı Eylemler */}
                      {userRole === 'super' && (
                        <div className="bg-white dark:bg-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Hızlı Eylemler
                          </h4>
                          <div className="space-y-2">
                            <button
                              onClick={() => {
                                openEditModal(selectedProjectForModal);
                                setIsProjectModalOpen(false);
                              }}
                              className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                              <Edit2 className="w-5 h-5 text-blue-500" />
                              <span className="text-gray-700 dark:text-gray-300">Danışmanlığı Düzenle</span>
                            </button>
                            
                            <button
                              onClick={() => {
                                openDeleteModal(selectedProjectForModal);
                                setIsProjectModalOpen(false);
                              }}
                              className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                              <X className="w-5 h-5 text-red-500" />
                              <span className="text-gray-700 dark:text-gray-300">Danışmanlığı Sil</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Danışmanlık/Alt Proje Oluşturma Modal */}
      <AnimatePresence>
        {isCreateModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsCreateModalOpen(false);
                setSelectedProjectType(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-2xl max-h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-y-auto"
            >
              <div className="p-6">
                {/* Üst Bar */}
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => {
                      if (selectedProjectType) {
                        setSelectedProjectType(null);
                      } else {
                        setIsCreateModalOpen(false);
                      }
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {!selectedProjectType ? 'Yeni Oluştur' : selectedProjectType === 'project' ? 'Yeni Firma Oluştur' : 'Firmaya Proje Oluştur'}
                  </h3>
                  <button
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      setSelectedProjectType(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                {/* İçerik */}
                {!selectedProjectType ? (
                  // Danışmanlık Tipi Seçimi
                  <div className="space-y-4">
                      {/* Danışmanlık */}
                    
                    <button
                      onClick={() => setSelectedProjectType('project')}
                      className="w-full p-4 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Folder className="w-6 h-6 text-primary-500" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">Firma Oluştur</h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Yeni bir Firma Ekleyin
                          </p>
                        </div>
                      </div>
                    </button>
                      
                      
                    <button
                      onClick={() => setSelectedProjectType('subproject')}
                      className="w-full p-4 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FolderPlus className="w-6 h-6 text-primary-500" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">Firmaya Proje Ekle</h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Firmaya Proje Oluşturun
                          </p>
                        </div>
                      </div>
                    </button>
                    {/*
                    <button
                      onClick={() => setSelectedProjectType('categories')}
                      className="w-full p-4 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Settings className="w-6 h-6 text-primary-500" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">Kategorileri Düzenle</h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Danışmanlık kategorilerini yönet
                          </p>
                        </div>
                      </div>
                    </button> */}
                  </div>
                ) : selectedProjectType === 'project' ? (
                  // Danışmanlık Oluşturma Formu
                  <div className="space-y-4">
                    {/* Danışmanlık Adı */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Firmanın Adı *
                      </label>
                      <input
                        type="text"
                        value={newProject.name}
                        onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        placeholder="Danışmanlık adını girin"
                      />
                    </div>

                    {/* Danışmanlık Açıklaması */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Firma Açıklaması (İsteğe Bağlı)
                      </label>
                      <textarea
                        value={newProject.description}
                        onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        placeholder="Danışmanlık açıklamasını girin"
                      />
                    </div>

                    {/* Tarihler */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Başlangıç Tarihi *
                        </label>
                        <input
                          type="date"
                          value={newProject.start_date}
                          onChange={(e) => {
                            const newStartDate = e.target.value;
                            setNewProject(prev => ({
                              ...prev,
                              start_date: newStartDate,
                              end_date: prev.end_date && newStartDate > prev.end_date ? '' : prev.end_date
                            }));
                          }}
                          max={newProject.end_date || undefined}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="hasEndDate"
                            checked={newProject.has_end_date}
                            onChange={(e) => {
                              setNewProject(prev => ({
                                ...prev,
                                has_end_date: e.target.checked,
                                end_date: e.target.checked ? prev.end_date : ''
                              }));
                            }}
                            className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label htmlFor="hasEndDate" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Bitiş Tarihi Belirle
                          </label>
                        </div>
                        
                        {newProject.has_end_date && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Bitiş Tarihi
                            </label>
                            <input
                              type="date"
                              value={newProject.end_date}
                              onChange={(e) => setNewProject({ ...newProject, end_date: e.target.value })}
                              min={newProject.start_date || undefined}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Kategoriler */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        HizmetKategorileri
                      </label>
                      {loadingStates.categories ? (
                        <div className="flex items-center justify-center p-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-lg">
                          {categories.map(category => (
                            <button
                              key={category.id}
                              onClick={() => {
                                setNewProject(prev => ({
                                  ...prev,
                                  categories: prev.categories.includes(category.id)
                                    ? prev.categories.filter(id => id !== category.id)
                                    : [...prev.categories, category.id]
                                }));
                              }}
                              className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                                newProject.categories.includes(category.id)
                                  ? `bg-${category.color}-50 text-${category.color}-700 dark:bg-${category.color}-900/20 dark:text-${category.color}-400 ring-1 ring-${category.color}-500`
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              <div className={`w-3 h-3 rounded-full bg-${category.color}-500`} />
                              <span className="text-sm">{category.name}</span>
                              {newProject.categories.includes(category.id) && (
                                <CheckCircle2 className="w-4 h-4 ml-auto" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Alt Projeler 
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Alt Projeler
                      </label>
                      {loadingStates.subProjects ? (
                        <div className="flex items-center justify-center p-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-40 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-lg">
                          {subProjects.all?.map(subProject => (
                            <button
                              key={subProject.id}
                              onClick={() => {
                                setNewProject(prev => ({
                                  ...prev,
                                  sub_projects: prev.sub_projects.includes(subProject.id)
                                    ? prev.sub_projects.filter(id => id !== subProject.id)
                                    : [...prev.sub_projects, subProject.id]
                                }));
                              }}
                              className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
                                newProject.sub_projects.includes(subProject.id)
                                  ? 'bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-500'
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{subProject.name}</span>
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  subProject.status === 'active'
                                    ? 'bg-green-50 text-green-700 dark:bg-green-900/20'
                                    : subProject.status === 'completed'
                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20'
                                    : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20'
                                }`}>
                                  {subProject.status === 'active' ? 'Aktif' : subProject.status === 'completed' ? 'Tamamlandı' : 'Beklemede'}
                                </span>
                              </div>
                              {newProject.sub_projects.includes(subProject.id) && (
                                <CheckCircle2 className="w-4 h-4 text-primary-500" />
                              )}
                            </button>
                          ))}
                          {(!subProjects.all || subProjects.all.length === 0) && (
                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                              Kullanılabilir alt proje bulunamadı
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    */}

                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        onClick={handleCreateProject}
                        disabled={loadingStates.creation}
                        className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingStates.creation ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Oluşturuluyor...</span>
                          </>
                        ) : (
                          <span>Danışmanlık Oluştur</span>
                        )}
                      </button>
                    </div>
                  </div>
                ) : selectedProjectType === 'subproject' ? (
                  // Alt Proje Oluşturma Formu
                  <div className="space-y-4">
                    {/* Danışmanlık Seçimi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Danışmanlık Seçin *
                      </label>
                      <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                        {projects.map((project) => (
                          <button
                            key={project.id}
                            onClick={() => setNewSubProject({
                              ...newSubProject,
                              parent_project_id: project.id
                            })}
                            className={`w-full p-3 text-left border rounded-lg transition-colors ${
                              newSubProject.parent_project_id === project.id
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {project.name}
                              </span>
                              {newSubProject.parent_project_id === project.id && (
                                <CheckCircle2 className="w-5 h-5 text-primary-500" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Alt Proje Adı */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Alt Proje Adı *
                      </label>
                      <input
                        type="text"
                        value={newSubProject.name}
                        onChange={(e) => setNewSubProject({ ...newSubProject, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        placeholder="Alt proje adını girin"
                      />
                    </div>

                    {/* Alt Proje Açıklaması */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Projenin Tanımı
                      </label>
                      <textarea
                        value={newSubProject.description}
                        onChange={(e) => setNewSubProject({ ...newSubProject, description: e.target.value })}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        placeholder="Alt proje açıklamasını girin (opsiyonel)"
                      />
                    </div>

                    {/* Tarihler */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Başlangıç Tarihi *
                        </label>
                        <input
                          type="date"
                          value={newSubProject.start_date}
                          onChange={(e) => {
                            const newStartDate = e.target.value;
                            setNewSubProject(prev => ({
                              ...prev,
                              start_date: newStartDate,
                              end_date: prev.end_date && newStartDate > prev.end_date ? '' : prev.end_date
                            }));
                          }}
                          max={newSubProject.end_date || undefined}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="hasEndDateSubProject"
                            checked={newSubProject.has_end_date}
                            onChange={(e) => {
                              setNewSubProject(prev => ({
                                ...prev,
                                has_end_date: e.target.checked,
                                end_date: e.target.checked ? prev.end_date : ''
                              }));
                            }}
                            className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                          />
                          <label htmlFor="hasEndDateSubProject" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Bitiş Tarihi Belirle
                          </label>
                        </div>
                        
                        {newSubProject.has_end_date && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Bitiş Tarihi
                            </label>
                            <input
                              type="date"
                              value={newSubProject.end_date}
                              onChange={(e) => setNewSubProject({ ...newSubProject, end_date: e.target.value })}
                              min={newSubProject.start_date || undefined}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        onClick={handleCreateSubProject}
                        disabled={loadingStates.creation}
                        className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingStates.creation ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Oluşturuluyor...</span>
                          </>
                        ) : (
                          <span>Alt Proje Oluştur</span>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  // Kategori Yönetimi Formu
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                        Danışmanlık Kategorileri
                      </h4>
                      <button
                        onClick={() => setIsNewCategoryModalOpen(true)}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Yeni Kategori</span>
                      </button>
                    </div>

                    {/* Kategori Listesi */}
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                      {loadingStates.categories ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>
                      ) : categories.length > 0 ? (
                        categories.map(category => (
                          <div key={category.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 dark:hover:border-primary-500 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: category.color }}></div>
                              <span className="text-gray-900 dark:text-white">{category.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              
                              <button
                                onClick={() => handleDeleteCategory(category.id)}
                                className="p-1 text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <Tags className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                          <p className="text-gray-500 dark:text-gray-400">
                            Henüz hiç kategori oluşturulmamış
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
                      <p>* Kategoriler danışmanlıkları gruplandırmak ve filtrelemek için kullanılır.</p>
                      <p>* Her kategoriye özel bir renk atayabilirsiniz.</p>
                      <p>* Silinen kategoriler, bu kategoriye sahip danışmanlıklarda görünmez olacaktır.</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Alt Proje Düzenleme Modal */}
      <AnimatePresence>
        {isSubProjectModalOpen && selectedProjectForModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsSubProjectModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-3xl max-h-[85vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Alt Projeleri Düzenle
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {selectedSubProjects.length} alt proje seçildi
                    </p>
                  </div>
                  <button
                    onClick={() => setIsSubProjectModalOpen(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Arama ve Filtreleme */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={subProjectSearch}
                        onChange={(e) => setSubProjectSearch(e.target.value)}
                        placeholder="Alt proje ara..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="min-w-[200px]">
                      <select
                        value={subProjectSort}
                        onChange={(e) => setSubProjectSort(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="name-asc">İsim (A-Z)</option>
                        <option value="name-desc">İsim (Z-A)</option>
                        <option value="status">Durum</option>
                        <option value="team-size">Ekip Büyüklüğü</option>
                      </select>
                    </div>
                  </div>

                  {/* Alt Projeler Listesi */}
                  <div className="grid grid-cols-1 gap-2 max-h-[calc(85vh-250px)] overflow-y-auto pr-2">
                    {(() => {
                      const filteredSubProjects = getFilteredAndSortedSubProjects(subProjects.all || [], subProjectSearch, subProjectSort);
                      
                      if (subProjectSearch && filteredSubProjects.length === 0) {
                        return (
                          <div className="text-center py-8">
                            <Search className="w-12 h-12 mx-auto text-gray-400 mb-3 animate-pulse" />
                            <p className="text-gray-500 dark:text-gray-400 animate-pulse">
                              "{subProjectSearch}" ile eşleşen alt proje bulunamadı
                            </p>
                          </div>
                        );
                      }

                      return filteredSubProjects.map((subProject) => (
                        <button
                          key={subProject.id}
                          onClick={() => {
                            if (selectedSubProjects.includes(subProject.id)) {
                              setSelectedSubProjects(prev => prev.filter(id => id !== subProject.id));
                            } else {
                              setSelectedSubProjects(prev => [...prev, subProject.id]);
                            }
                          }}
                          className={`w-full p-4 text-left border rounded-lg transition-all ${
                            selectedSubProjects.includes(subProject.id)
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-500'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {subProject.name}
                                </span>
                                <div
                                  className={`px-2 py-0.5 text-xs rounded-full ${
                                    subProject.status === 'active'
                                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                      : subProject.status === 'completed'
                                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                                      : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                                  }`}
                                >
                                  {subProject.status === 'active'
                                    ? 'Aktif'
                                    : subProject.status === 'completed'
                                    ? 'Tamamlandı'
                                    : 'Beklemede'}
                                </div>
                              </div>
                              {subProject.description && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                  {subProject.description}
                                </p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                                <div className="flex items-center gap-1">
                                  <Users2 className="w-4 h-4" />
                                  <span>{subProject.team_size || 0} Üye</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <BarChart2 className="w-4 h-4" />
                                  <span>{subProject.task_count || 0} Görev</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <CheckCircle2 className="w-4 h-4" />
                                  <span>{subProject.completed_tasks || 0} Tamamlanan</span>
                                </div>
                              </div>
                            </div>
                            {selectedSubProjects.includes(subProject.id) && (
                              <CheckCircle2 className="w-5 h-5 text-primary-500 flex-shrink-0 ml-4" />
                            )}
                          </div>
                        </button>
                      ));
                    })()}
                  </div>

                  {/* Alt Bilgi ve Butonlar */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700 ">
                    <div className="text-sm text-gray-500 dark:text-gray-400 ">
                      {subProjectSearch ? (
                        <>
                          Aranan "{subProjectSearch}" için {getFilteredAndSortedSubProjects(subProjects.all || [], subProjectSearch, subProjectSort).length} sonuç bulundu
                        </>
                      ) : (
                        <>Toplam <strong>{subProjects.all?.length || 0}</strong> alt proje mevcut</>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setIsSubProjectModalOpen(false)}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        İptal
                      </button>
                      <button
                        onClick={async () => {
                          await handleUpdateSubProjects(selectedProjectForModal.id, selectedSubProjects);
                          setIsSubProjectModalOpen(false);
                        }}
                        disabled={loadingStates.subProjects}
                        className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                      >
                        {loadingStates.subProjects ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Kaydediliyor...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4" />
                            <span>Değişiklikleri Kaydet</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Yeni Kategori Oluşturma Modal */}
      <AnimatePresence>
        {isNewCategoryModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsNewCategoryModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-2xl max-h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Yeni Kategori Oluştur
                  </h3>
                  <button
                    onClick={() => setIsNewCategoryModalOpen(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Kategori Adı *
                    </label>
                    <input
                      type="text"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      placeholder="Kategori adını girin"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setIsNewCategoryModalOpen(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleCreateCategory}
                      disabled={loadingStates.categories}
                      className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                      {loadingStates.categories ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Oluşturuluyor...</span>
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          <span>Kategori Oluştur</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Danışmanlık Düzenleme Modal */}
      <AnimatePresence>
        {isEditModalOpen && editingProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingProject(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-2xl max-h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Danışmanlık Düzenle
                  </h3>
                  <button
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setEditingProject(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Firma Adı */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Firma Adı *
                    </label>
                    <input
                      type="text"
                      value={editingProject.name}
                      onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      placeholder="Firma adını girin"
                    />
                  </div>

                  {/* Firma Açıklaması */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Firma Açıklaması
                    </label>
                    <textarea
                      value={editingProject.description || ''}
                      onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      placeholder="Firma açıklamasını girin"
                    />
                  </div>

                  {/* Durum */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Danışmanlık Durumu
                    </label>
                    <select
                      value={editingProject.status}
                      onChange={(e) => setEditingProject({ ...editingProject, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="active">Aktif</option>
                      <option value="completed">Tamamlandı</option>
                      <option value="pending">Beklemede</option>
                    </select>
                  </div>

                  {/* Tarihler */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Başlangıç Tarihi *
                      </label>
                      <input
                        type="date"
                        value={editingProject.start_date}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          setEditingProject(prev => ({
                            ...prev,
                            start_date: newStartDate,
                            end_date: prev.end_date && newStartDate > prev.end_date ? '' : prev.end_date
                          }));
                        }}
                        max={editingProject.end_date || undefined}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="editHasEndDate"
                          checked={editingProject.has_end_date}
                          onChange={(e) => {
                            setEditingProject(prev => ({
                              ...prev,
                              has_end_date: e.target.checked,
                              end_date: e.target.checked ? prev.end_date : ''
                            }));
                          }}
                          className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <label htmlFor="editHasEndDate" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Bitiş Tarihi Belirle
                        </label>
                      </div>
                      
                      {editingProject.has_end_date && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Bitiş Tarihi
                          </label>
                          <input
                            type="date"
                            value={editingProject.end_date || ''}
                            onChange={(e) => setEditingProject({ ...editingProject, end_date: e.target.value })}
                            min={editingProject.start_date || undefined}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => {
                        setIsEditModalOpen(false);
                        setEditingProject(null);
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleEditProject}
                      disabled={loadingStates.creation}
                      className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {loadingStates.creation ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Güncelleniyor...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Güncelle</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Danışmanlık Silme Onay Modal */}
      <AnimatePresence>
        {isDeleteModalOpen && projectToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setProjectToDelete(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Danışmanlık Sil
                  </h3>
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setProjectToDelete(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    <strong>"{projectToDelete.name}"</strong> danışmanlığını silmek istediğinizden emin misiniz?
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Bu işlem geri alınamaz ve danışmanlığa ait tüm veriler (alt projeler, kategoriler) silinecektir.
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setProjectToDelete(null);
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleDeleteProject}
                    disabled={loadingStates.creation}
                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loadingStates.creation ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Siliniyor...</span>
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4" />
                        <span>Sil</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Alt Proje Düzenleme Modal */}
      <AnimatePresence>
        {isEditSubProjectModalOpen && editingSubProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsEditSubProjectModalOpen(false);
                setEditingSubProject(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-2xl max-h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Alt Proje Düzenle
                  </h3>
                  <button
                    onClick={() => {
                      setIsEditSubProjectModalOpen(false);
                      setEditingSubProject(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Firma Adı */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Firma Adı *
                    </label>
                    <input
                      type="text"
                      value={editingSubProject.name}
                      onChange={(e) => setEditingSubProject({ ...editingSubProject, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      placeholder="Firma adını girin"
                    />
                  </div>

                  {/* Firma Açıklaması */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Firma Açıklaması
                    </label>
                    <textarea
                      value={editingSubProject.description || ''}
                      onChange={(e) => setEditingSubProject({ ...editingSubProject, description: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      placeholder="Firma açıklamasını girin"
                    />
                  </div>

                  {/* Durum */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Danışmanlık Durumu
                    </label>
                    <select
                      value={editingSubProject.status}
                      onChange={(e) => setEditingSubProject({ ...editingSubProject, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="active">Aktif</option>
                      <option value="completed">Tamamlandı</option>
                      <option value="pending">Beklemede</option>
                    </select>
                  </div>

                  {/* Tarihler */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Başlangıç Tarihi *
                      </label>
                      <input
                        type="date"
                        value={editingSubProject.start_date}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          setEditingSubProject(prev => ({
                            ...prev,
                            start_date: newStartDate,
                            end_date: prev.end_date && newStartDate > prev.end_date ? '' : prev.end_date
                          }));
                        }}
                        max={editingSubProject.end_date || undefined}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="editHasEndDateSubProject"
                          checked={editingSubProject.has_end_date}
                          onChange={(e) => {
                            setEditingSubProject(prev => ({
                              ...prev,
                              has_end_date: e.target.checked,
                              end_date: e.target.checked ? prev.end_date : ''
                            }));
                          }}
                          className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <label htmlFor="editHasEndDateSubProject" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Bitiş Tarihi Belirle
                        </label>
                      </div>
                      
                      {editingSubProject.has_end_date && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Bitiş Tarihi
                          </label>
                          <input
                            type="date"
                            value={editingSubProject.end_date || ''}
                            onChange={(e) => setEditingSubProject({ ...editingSubProject, end_date: e.target.value })}
                            min={editingSubProject.start_date || undefined}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => {
                        setIsEditSubProjectModalOpen(false);
                        setEditingSubProject(null);
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleEditSubProject}
                      disabled={loadingStates.creation}
                      className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {loadingStates.creation ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Güncelleniyor...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Güncelle</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Alt Proje Silme Modal */}
      <AnimatePresence>
        {isDeleteSubProjectModalOpen && subProjectToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                setIsDeleteSubProjectModalOpen(false);
                setSubProjectToDelete(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative w-[90vw] max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Alt Proje Sil
                  </h3>
                  <button
                    onClick={() => {
                      setIsDeleteSubProjectModalOpen(false);
                      setSubProjectToDelete(null);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    <strong>"{subProjectToDelete.name}"</strong> alt projesi silmek istediğinizden emin misiniz?
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Bu işlem geri alınamaz ve alt projeye ait tüm veriler silinecektir.
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setIsDeleteSubProjectModalOpen(false);
                      setSubProjectToDelete(null);
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleDeleteSubProject}
                    disabled={loadingStates.creation}
                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loadingStates.creation ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Siliniyor...</span>
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4" />
                        <span>Sil</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Consulting;
